/**
 * Create or edit a survey.
 *
 * Four blocks, and the split is the point. **Identificação** is what the survey *is*: the BU, the
 * slug, the use case. **Entrega** is when and how it shows up. **Elegibilidade** is who gets it,
 * written as the sentence that rule actually is. **Questionário** is what people read, edited as
 * fields or as JSON, in whichever language — and `layout` lives there, because deciding whether a
 * step is a screen is a decision about the questionnaire, not about when it appears.
 *
 * Nothing in the first two blocks can arrive from a pasted document: a slug or an opt-in condition
 * coming in with the content is the easiest way to aim a survey at the wrong people.
 *
 * Two things this screen has to keep saying out loud, because the interface looks the same either
 * way and the consequence is not:
 *
 * - editing an approved survey opens a **draft version**, and the version already live keeps being
 *   delivered until the draft is approved (R4) — said before the save, in a confirmation, and
 *   again in the banner while the draft is open;
 * - a structural edit on a survey with answers comes back `409`, and the version dialog is where
 *   the `reask` decision is made (R1/R3).
 *
 * Two ways back out live here too, and they are not variants of each other. **Descartar rascunho**
 * (R41) is the author binning their own draft: it needs no reviewer and writes no review stamp, so
 * it sits beside the draft banner rather than beside Rejeitar. **Resetar pesquisa** (R42) puts the
 * survey back the way it was created — answers *and* versions — behind the same locks as the purge,
 * and is absent, not disabled, without `surveys_purge` and `survey:purge`.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTSelect, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useCallback, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SurveyDiscardDraftDialog } from './Components/SurveyDiscardDraftDialog';
import { SurveyDraftVersionDialog } from './Components/SurveyDraftVersionDialog';
import { SurveyEligibilityBlock } from './Components/SurveyEligibilityBlock';
import { SurveyPreviewModal } from './Components/SurveyPreviewModal';
import { SurveyQuestionsBlock } from './Components/SurveyQuestionsBlock';
import { SurveyRejectDialog } from './Components/SurveyRejectDialog';
import { SurveyResetDialog } from './Components/SurveyResetDialog';
import { SurveyVersionDialog } from './Components/SurveyVersionDialog';
import { SURVEY_PURGE } from '../../Constants/permissions';
import { useDiscardSurveyDraft } from '../../Hooks/useDiscardSurveyDraft';
import { useResetSurvey } from '../../Hooks/useResetSurvey';
import { useRestoreSurvey } from '../../Hooks/useRestoreSurvey';
import { useSurveyBuilderForm } from '../../Hooks/useSurveyBuilderForm';
import type { SurveyDetail, SurveyLanguage, SurveyTrigger } from '../../Types/Surveys.types';
import { SURVEY_LANGUAGE_LABELS } from '../../Types/Surveys.types';
import type { SurveyFormData, TargetingFeature } from '../../Utils/surveyForm';
import { allowedUseCaseIds, findUseCaseConflict } from '../../Utils/surveyForm';
import {
  APPROVAL_LABELS,
  APPROVAL_TONES,
  TRIGGER_HINTS,
  TRIGGER_LABELS,
  triggerFieldState,
  versionNotice,
} from '../../Utils/surveyFormat';
import { useProfile } from '@/Auth/contexts/ProfileContext';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { RestoreHolderDialog } from '@/Shared/components/RestoreHolderDialog/RestoreHolderDialog';
import { useTenants } from '@/Hooks/useTenants';
import { useOptInFeatures } from '@/Pages/BUs/OptInFeatures/Hooks/useOptInFeatures';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';
import { Switch } from '@/Shared/components/Switch/Switch';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const layoutStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  max-width: 1120px;
`;

const panelStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-xs);
  padding: var(--space-ml);
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  min-width: 0;
`;

const panelHeadStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const panelTitleStyle = css`
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--letter-spacing-tight);
`;

const panelHintStyle = css`
  margin: 0;
  flex: 1 1 260px;
  min-width: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

/**
 * Fields sized by their content, not by a share of the grid.
 *
 * A priority is one digit and a date is a date; giving both a quarter of a twelve-column row makes
 * the short ones look unfinished and puts the label a long way from the box it names.
 */
const fieldsRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--space-m) var(--space-ml);
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const fieldLabelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

/** Lighter and unbolded, so "(opcional)" reads as an aside to the label, not as part of it. */
const optionalHintStyle = css`
  font-weight: var(--font-weight-regular);
  color: var(--color-text-muted);
`;

const hintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

const selectStyle = css`
  height: 40px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-sm);
  min-width: 0;
`;

const dateInputStyle = css`
  height: 40px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-family: inherit;
  min-width: 0;
`;

/** Keeps the two ends of the collection window on the same line. */
const datePairStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-m);
`;

/** A number with its unit beside it: "volta em [7] dias" reads as a phrase, not as a field. */
const suffixRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-xs);
`;

const suffixFieldStyle = css`
  display: block;
  width: 120px;
`;

const suffixStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const switchRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-m);
  padding: var(--space-s) var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const switchTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const switchLabelStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const switchHintStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const statusBarStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const noticeStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-s);
  padding: var(--space-m);
  border: 1px solid var(--color-info);
  border-left: 3px solid var(--color-info);
  border-radius: var(--radius-m);
  background: var(--color-info-subtle);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

/** Lets the sentence take the row and pushes the discard button to the far end of the banner. */
const draftNoticeTextStyle = css`
  flex: 1;
  min-width: 0;
`;

const alertStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-danger);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-m);
  background: var(--color-danger-subtle);
  font-size: var(--font-size-xs);
`;

const alertTitleStyle = css`
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
`;

const spacerStyle = css`
  flex: 1;
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-base);
  flex-wrap: wrap;
  padding-bottom: var(--space-m);
`;

const blockedStyle = css`
  flex: 1 1 320px;
  min-width: 0;
  font-size: var(--font-size-xs);
  color: var(--gl-yellow-dark);
  text-wrap: pretty;
`;

const moderationStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

export interface SurveyBuilderProps {
  /** The survey being edited. Absent on `/surveys/new`. */
  survey?: SurveyDetail;
  onDone: () => void;
  onCreated: (id: string) => void;
}

export const SurveyBuilder = ({ survey, onDone, onCreated }: SurveyBuilderProps): JSX.Element => {
  const { tenants, tenantOptions } = useTenants();
  const [searchParams] = useSearchParams();
  const { pages: privileges } = usePrivileges();
  const { profile } = useProfile();

  /**
   * Every opt-in feature, fetched before the form exists and filtered by BU in memory.
   *
   * Asking the BFF per tenant would need `form.tenantSlug`, which the hook below produces — and
   * the hook needs this list to spot the targeting contradiction. One unfiltered request breaks
   * the circle. Surveys depend on opt-in, never the other way (R23).
   */
  const { data: featuresPage } = useOptInFeatures();

  const allFeatures = useMemo(
    () =>
      (featuresPage?.data ?? []).map(feature => ({
        id: feature.id,
        name: feature.name,
        key: feature.key,
        tenantSlug: feature.tenantSlug,
        useCases: (feature.useCases ?? []).map(useCase => ({
          id: useCase.id,
          name: useCase.name,
        })),
      })),
    [featuresPage],
  );

  const findTargetingConflict = useCallback(
    (current: SurveyFormData) => {
      const tenant = tenants.find(item => item.slug === current.tenantSlug);
      const tenantUseCases = (tenant?.useCases ?? []).map(useCase => ({
        id: useCase.id,
        name: useCase.name,
      }));

      return findUseCaseConflict(current, allFeatures as TargetingFeature[], tenantUseCases);
    },
    [allFeatures, tenants],
  );

  /**
   * "Criar questionário de saída", arriving from an opt-in feature.
   *
   * The feature knows its own BU and its own id, and making somebody pick them again on a form
   * that lists every feature of every BU is how a questionnaire ends up attached to the feature
   * next to the one it was written for. Read once, for a new survey only.
   */
  const initialDraft = useMemo<Partial<SurveyFormData> | undefined>(() => {
    const featureId = searchParams.get('optInFeatureId');

    if (survey || !featureId) return undefined;

    const tenantSlug = searchParams.get('tenantSlug');
    /*
      The key travels in the URL rather than being looked up from the feature list, because this
      runs before that list arrives — and a slug filled in later, by an effect, would land on top
      of whatever had already been typed.
    */
    const featureKey = searchParams.get('optInFeatureKey');

    return {
      trigger: 'ON_OPT_OUT',
      conditionOnOptIn: true,
      requiredOptInState: 'DISABLED',
      requiresOptInFeatureId: featureId,
      ...(tenantSlug ? { tenantSlug } : {}),
      // A suggestion, not a rule: the field stays editable, and the slug is the survey's own name.
      ...(featureKey ? { slug: `${featureKey}-saida` } : {}),
    };
  }, [searchParams, survey]);

  /**
   * Whether this form was opened by "Criar questionário de saída".
   *
   * Two of its fields stop being choices: the BU is the feature's BU, and the trigger is the whole
   * reason the person is here. Leaving them open invites attaching the questionnaire to a feature
   * in another BU, or to nothing — and the second is silent, because a survey with no trigger of
   * this kind simply never reaches the modal.
   */
  const isExitDraft = Boolean(initialDraft);

  const builder = useSurveyBuilderForm({
    survey,
    onCreated,
    findTargetingConflict,
    initialDraft,
  });
  const restoreSurvey = useRestoreSurvey();
  const discardDraft = useDiscardSurveyDraft(survey?.id ?? '');
  const resetSurvey = useResetSurvey(survey?.id ?? '');
  const { form, errors } = builder;

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isDiscardingDraft, setIsDiscardingDraft] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  /*
    Two gates, as everywhere else in this panel: the `surveys_purge` flag says this screen offers
    the reset, and `survey:purge` on the profile says the BFF would allow it. Either one missing
    makes the button **absent**, not disabled — a flag alone would render something that answers
    `403`, and a permission alone would ignore the switch in Flagsmith.

    It is `survey:purge` and not a fourth permission on purpose (R42): whoever may erase the answers
    may erase the answers and the versions. The damage here is a superset of the purge's, not a
    different kind.
  */
  const canReset =
    privileges.BUSINESS_UNITS.SURVEYS.PURGE &&
    (profile?.permissions?.includes(SURVEY_PURGE) ?? false);

  const featureOptions = useMemo(
    () =>
      allFeatures
        .filter(feature => !form.tenantSlug || feature.tenantSlug === form.tenantSlug)
        .map(feature => ({
          id: feature.id,
          name: feature.name,
          key: feature.key,
        })),
    [allFeatures, form.tenantSlug],
  );

  const tenantSelectOptions = useMemo(
    () =>
      tenantOptions.map(option => ({
        id: String(option.id),
        text: option.text,
        value: String(option.value),
      })),
    [tenantOptions],
  );

  const selectedTenant = tenantSelectOptions.filter(option => option.value === form.tenantSlug);

  const tenantUseCases = useMemo(() => {
    const tenant = tenants.find(item => item.slug === form.tenantSlug);

    return (tenant?.useCases ?? [])
      .filter(useCase => useCase.isActive)
      .map(useCase => ({ id: useCase.id, name: useCase.name }));
  }, [tenants, form.tenantSlug]);

  /**
   * With an opt-in condition on, the survey may only be narrowed **inside** the feature's audience.
   *
   * The two conditions are joined with AND: aiming the survey at a use case the feature is not
   * offered to makes the intersection empty, and an empty audience is a survey that is approved,
   * active, in its window — and reaches nobody, silently. Restricting the list stops the
   * contradiction from being built in the first place; `null` means the feature is offered to
   * everyone, so there is nothing to restrict.
   */
  const allowedIds = useMemo(
    () => allowedUseCaseIds(form, allFeatures as TargetingFeature[]),
    [allFeatures, form],
  );

  const useCaseOptions = useMemo(
    () =>
      allowedIds === null
        ? tenantUseCases
        : tenantUseCases.filter(useCase => allowedIds.includes(useCase.id)),
    [allowedIds, tenantUseCases],
  );

  const conflict = builder.targetingConflict;

  const isAutoTrigger = form.trigger === 'AUTO';
  /*
    R44 — the trigger stops being editable once somebody has been asked something under it, and not
    a moment earlier. The count is the survey's own `responseCount`, which is every version and
    every status: the same number the purge dialog states and the same line the BFF draws. The
    version cards on this screen cover one version, so unlocking against them would let somebody
    swap the trigger of a bumped survey and take the `409` afterwards.
  */
  const triggerField = triggerFieldState(survey, isExitDraft);
  const draftNotice = survey ? versionNotice(survey) : null;
  const reviewStatus = survey?.reviewStatus;

  return (
    <MainContent id='survey-builder-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={layoutStyle}>
        {survey ? (
          <section className={panelStyle}>
            <div className={statusBarStyle}>
              <StatusTag
                id='survey-builder-status'
                status={APPROVAL_TONES[reviewStatus ?? survey.approvalStatus]}
                dot
              >
                {APPROVAL_LABELS[reviewStatus ?? survey.approvalStatus]}
              </StatusTag>
              <span className={hintStyle}>
                Versão {survey.workingVersion} em edição · versão {survey.version} entregue ·{' '}
                {survey.responseCount} respostas
              </span>
              <div className={spacerStyle} />

              <div className={moderationStyle}>
                {privileges.BUSINESS_UNITS.SURVEYS.SUBMIT &&
                (reviewStatus === 'DRAFT' || reviewStatus === 'REJECTED') ? (
                  <UTButton
                    fitContent
                    variant='secondary'
                    id='survey-builder-submit-approval'
                    text='Enviar para aprovação'
                    icon='Communication.PaperPlaneTilt'
                    loading={builder.isModerating}
                    onClick={builder.submitForApproval}
                  />
                ) : null}
                {privileges.BUSINESS_UNITS.SURVEYS.APPROVE &&
                reviewStatus === 'PENDING_APPROVAL' ? (
                  <>
                    <UTButton
                      fitContent
                      variant='secondary'
                      id='survey-builder-reject'
                      text='Rejeitar'
                      loading={builder.isModerating}
                      onClick={() => {
                        setIsRejecting(true);
                      }}
                    />
                    <UTButton
                      fitContent
                      id='survey-builder-approve'
                      text='Aprovar'
                      icon='Communication.CheckCircle'
                      loading={builder.isModerating}
                      onClick={builder.approve}
                    />
                  </>
                ) : null}
                {/*
                  Resetting lives beside the version line, because that line is what it rewrites:
                  "versão 3 em edição · versão 2 entregue · 14 respostas" becomes "versão 1 · sem
                  rascunho · 0 respostas". Tertiary and last, so it never competes with Aprovar.
                */}
                {canReset ? (
                  <UTButton
                    fitContent
                    variant='tertiary'
                    id='survey-builder-reset'
                    text='Resetar pesquisa'
                    icon='Arrows.ArrowCounterClockwise'
                    state='danger'
                    onClick={() => {
                      setIsResetting(true);
                    }}
                  />
                ) : null}
              </div>
            </div>

            {/*
              The banner explains the draft, so the way out of the draft belongs beside it — and
              deliberately NOT beside Rejeitar. Discarding is the author binning their own draft and
              writes no review stamp; rejecting is the reviewer's verdict (R41). Two buttons sitting
              together in the moderation row would read as two ways of doing the same thing.
            */}
            {draftNotice && survey.draftVersion !== null ? (
              <div id='survey-builder-draft-notice' className={noticeStyle}>
                <GLIcon variant='Communication.Info' size={16} fill='var(--color-info)' />
                <span className={draftNoticeTextStyle}>{draftNotice}</span>
                <UTButton
                  fitContent
                  variant='tertiary'
                  size='small'
                  id='survey-builder-discard-draft'
                  text='Descartar rascunho'
                  icon='Arrows.ArrowBendUpLeft'
                  onClick={() => {
                    setIsDiscardingDraft(true);
                  }}
                />
              </div>
            ) : null}

            {survey.rejectionReason && reviewStatus === 'REJECTED' ? (
              <div id='survey-builder-rejection' className={alertStyle} role='alert'>
                <span className={alertTitleStyle}>Rejeitada</span>
                <span>{survey.rejectionReason}</span>
              </div>
            ) : null}

            {builder.missingLanguages ? (
              <div id='survey-builder-missing-languages' className={alertStyle} role='alert'>
                <span className={alertTitleStyle}>
                  Falta tradução em{' '}
                  {builder.missingLanguages
                    .map(code => SURVEY_LANGUAGE_LABELS[code as SurveyLanguage] ?? code)
                    .join(' e ')}
                </span>
                <span>
                  A aprovação foi recusada porque parte do público leria o texto em inglês sem
                  ninguém perceber. Abra a aba do idioma que falta, preencha o que o painel de
                  pendências lista e tente de novo.
                </span>
                <div>
                  <UTButton
                    fitContent
                    variant='tertiary'
                    size='small'
                    id='survey-builder-missing-dismiss'
                    text='Entendi'
                    onClick={builder.dismissMissingLanguages}
                  />
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className={panelStyle}>
          <div className={panelHeadStyle}>
            <h2 className={panelTitleStyle}>Identificação</h2>
            <p className={panelHintStyle}>
              Que pesquisa é esta — e só isso. Quem recebe fica em Elegibilidade, num lugar só. O
              slug é o nome estável dela, e é o que um purge pede de volta por escrito; a BU fica
              travada assim que a pesquisa existe.
            </p>
          </div>

          <div className={fieldsRowStyle}>
            <div className={fieldStyle} style={{ width: 260 }}>
              <span className={fieldLabelStyle}>BU</span>
              {builder.isEditing || isExitDraft ? (
                <UTTextField id='survey-builder-tenant' value={form.tenantSlug} disabled />
              ) : (
                <UTSelect
                  /*
                    Remounted once the tenants arrive: `defaultSelectedOptions` is read at mount,
                    and a BU pre-filled from "Criar questionário de saída" would otherwise show an
                    empty box over a form that already has one chosen.
                  */
                  key={`tenant-${tenantSelectOptions.length}`}
                  options={tenantSelectOptions}
                  canSelectMultiple={false}
                  hasCheckbox={false}
                  defaultSelectedOptions={selectedTenant}
                  inputOptions={{
                    textFieldProps: {
                      id: 'survey-builder-tenant',
                      placeholder: 'Escolha a BU',
                    },
                  }}
                  onSelectedOptionsChange={options => {
                    builder.setField(
                      'tenantSlug',
                      options.length > 0 ? String(options[0].value) : '',
                    );
                  }}
                />
              )}
              {errors.tenantSlug ? <span className={errorStyle}>{errors.tenantSlug}</span> : null}
            </div>

            <div className={fieldStyle} style={{ width: 280 }}>
              <span className={fieldLabelStyle}>Slug</span>
              <UTTextField
                id='survey-builder-slug'
                placeholder='satisfacao-mdm'
                value={form.slug}
                state={errors.slug ? 'error' : 'normal'}
                onChange={event => {
                  builder.setSlug(event.target.value);
                }}
              />
              {errors.slug ? (
                <span className={errorStyle}>{errors.slug}</span>
              ) : (
                <span className={hintStyle}>Minúsculas e hífen.</span>
              )}
            </div>
          </div>
        </section>

        <section className={panelStyle}>
          <div className={panelHeadStyle}>
            <h2 className={panelTitleStyle}>Entrega</h2>
            <p className={panelHintStyle}>
              Quando ela aparece. Sem datas, vale enquanto estiver ativa e aprovada. Existe ainda um
              intervalo entre pesquisas automáticas para a mesma pessoa, de 14 dias, definido por BU
              e não editável aqui: a prioridade escolhe <strong>qual</strong> pesquisa, o intervalo
              decide <strong>se agora</strong>.
            </p>
          </div>

          <div className={fieldsRowStyle}>
            {/* Wider only when there is a sentence under it: the box itself stays 220. */}
            <div className={fieldStyle} style={{ width: triggerField.notice ? 420 : 220 }}>
              <span className={fieldLabelStyle}>Gatilho</span>
              {/*
                Locked in two cases, for two reasons — and never mutely: a disabled box with no
                explanation sends somebody looking for the condition that opens it, and here that
                condition is a number.

                Coming from an opt-in feature: the trigger is the reason this form was opened, and
                changing it there would leave a survey pointing at a feature it no longer asks
                anything about. Nothing unlocks that one.

                A saved survey **that already has answers** (R44): the trigger decides WHO is asked
                and WHEN, so changing it rewrites what the answers already collected mean. A survey
                turned into an exit questionnaire keeps responses given by people who were using the
                feature, not leaving it — two different audiences at two different moments, pooled
                under one slug with nothing saying so. Versioning does not cover this: it clones the
                questionnaire, and the questionnaire is not what changed.

                With no answer at all it stays **editable**, because the BFF accepts it: there is no
                collected data to reinterpret, and refusing here would make picking the wrong option
                on a survey created minutes ago cost deleting it and starting over.
              */}
              <select
                id='survey-builder-trigger'
                className={selectStyle}
                style={{ width: 220 }}
                value={form.trigger}
                disabled={triggerField.isLocked}
                aria-describedby={triggerField.notice ? 'survey-builder-trigger-lock' : undefined}
                onChange={event => {
                  builder.setTrigger(event.target.value as SurveyTrigger);
                }}
              >
                {(['AUTO', 'ON_DEMAND', 'ON_OPT_OUT'] as SurveyTrigger[]).map(trigger => (
                  <option key={trigger} value={trigger}>
                    {TRIGGER_LABELS[trigger]}
                  </option>
                ))}
              </select>
              {triggerField.notice ? (
                <span id='survey-builder-trigger-lock' className={hintStyle}>
                  {triggerField.notice}
                </span>
              ) : null}
            </div>

            {/* Both only mean something for a survey that opens by itself: priority breaks a tie
                between automatic surveys, and a snooze governs what happens after someone closes
                one they did not ask for. Under "sob demanda" they are two boxes with no meaning. */}
            {isAutoTrigger ? (
              <div className={fieldStyle} style={{ width: 176 }}>
                <span className={fieldLabelStyle}>Prioridade entre automáticas</span>
                <UTTextField
                  id='survey-builder-priority'
                  type='number'
                  value={String(form.priority)}
                  onChange={event => {
                    builder.setField('priority', Number(event.target.value) || 0);
                  }}
                />
              </div>
            ) : null}

            {isAutoTrigger ? (
              <div className={fieldStyle} style={{ width: 268 }}>
                <span className={fieldLabelStyle}>Se fechar sem responder, volta em</span>
                <div className={suffixRowStyle}>
                  <span className={suffixFieldStyle}>
                    <UTTextField
                      id='survey-builder-snooze-days'
                      type='number'
                      placeholder='não volta'
                      value={form.snoozeDays}
                      onChange={event => {
                        builder.setField('snoozeDays', event.target.value);
                      }}
                    />
                  </span>
                  <span className={suffixStyle}>dias</span>
                </div>
              </div>
            ) : null}

            {/* The two dates are one setting, so they wrap together: "Coleta de … até …" split
                across two rows stops reading as a window. */}
            <div className={datePairStyle}>
              <div className={fieldStyle} style={{ width: 216 }}>
                {/*
                  Said on the field, not only in the panel's paragraph above: an empty date box
                  next to a filled form reads as something still missing, and the person who reads
                  the paragraph is not the one staring at the box.
                */}
                <span className={fieldLabelStyle}>
                  Coleta de <span className={optionalHintStyle}>(opcional)</span>
                </span>
                <input
                  id='survey-builder-opens-at'
                  type='datetime-local'
                  className={dateInputStyle}
                  value={form.opensAt}
                  onChange={event => {
                    builder.setField('opensAt', event.target.value);
                  }}
                />
              </div>

              <div className={fieldStyle} style={{ width: 216 }}>
                <span className={fieldLabelStyle}>
                  até <span className={optionalHintStyle}>(opcional)</span>
                </span>
                <input
                  id='survey-builder-closes-at'
                  type='datetime-local'
                  className={dateInputStyle}
                  value={form.closesAt}
                  onChange={event => {
                    builder.setField('closesAt', event.target.value);
                  }}
                />
              </div>
            </div>
          </div>

          {/*
            What the chosen trigger actually commits to. `ON_OPT_OUT` is the one that cannot be
            read off its own name: it is not "opens when the feature is turned off" in the sense of
            joining the queue at that moment — it never joins the queue, and the only route to it is
            the feature it names.
          */}
          <div id='survey-builder-trigger-hint' className={noticeStyle}>
            <GLIcon variant='Communication.Info' size={16} fill='var(--color-info)' />
            <span>{TRIGGER_HINTS[form.trigger]}</span>
          </div>

          <div className={switchRowStyle}>
            <Switch
              id='survey-builder-active'
              checked={form.isActive}
              aria-label='Pesquisa ativa'
              onChange={checked => {
                builder.setField('isActive', checked);
              }}
            />
            <div className={switchTextStyle}>
              <span className={switchLabelStyle}>
                {form.isActive ? 'Pesquisa ativa' : 'Pesquisa desativada'}
              </span>
              <span className={switchHintStyle}>
                Desativada, ela para de ser entregue na hora, mesmo aprovada e dentro da janela. As
                respostas já coletadas ficam. É também a saída para salvar um questionário pela
                metade: desativada, ela não chega a ninguém.
              </span>
            </div>
          </div>
        </section>

        <section className={panelStyle}>
          <SurveyEligibilityBlock
            form={form}
            featureOptions={featureOptions}
            useCaseOptions={useCaseOptions}
            conflict={conflict}
            isExitQuestionnaire={form.trigger === 'ON_OPT_OUT'}
            error={errors.optInFeature}
            onToggle={value => {
              builder.setField('conditionOnOptIn', value);
            }}
            onFeatureChange={value => {
              builder.setField('requiresOptInFeatureId', value);
            }}
            onUseCaseChange={value => {
              builder.setField('useCaseId', value);
            }}
            onStateChange={value => {
              builder.setField('requiredOptInState', value);
            }}
            onDelayAmountChange={value => {
              builder.setField('delayAmount', value);
            }}
            onDelayUnitChange={value => {
              builder.setField('delayUnit', value);
            }}
            onAnchorChange={value => {
              builder.setField('delayAnchor', value);
            }}
            onEligibleFromChange={value => {
              builder.setField('eligibleOptInFrom', value);
            }}
            onMaxDaysChange={value => {
              builder.setField('maxDaysSinceAnchor', value);
            }}
          />
        </section>

        <section className={panelStyle}>
          <SurveyQuestionsBlock
            builder={builder}
            onPreview={() => {
              setIsPreviewOpen(true);
            }}
          />
          {errors.questions ? <span className={errorStyle}>{errors.questions}</span> : null}
        </section>

        <div className={footerStyle}>
          {builder.canSave ? null : (
            <span id='survey-builder-blocked' className={blockedStyle}>
              {conflict
                ? `Do jeito que está, esta pesquisa não alcança ninguém: ${conflict.featureName} não é oferecida ao use case ${conflict.useCaseName}. `
                : `${builder.problems.length} ${
                    builder.problems.length === 1 ? 'pendência' : 'pendências'
                  } acima. `}
              Resolva, ou desative a pesquisa para salvar como rascunho — desativada ela não é
              entregue a ninguém.
            </span>
          )}
          <UTButton
            fitContent
            variant='secondary'
            id='survey-builder-cancel'
            text='Cancelar'
            onClick={onDone}
          />
          <UTButton
            fitContent
            id='survey-builder-save'
            text={builder.isEditing ? 'Salvar alterações' : 'Criar pesquisa'}
            loading={builder.isSaving}
            disabled={!builder.canSave}
            title={
              builder.canSave
                ? undefined
                : `Faltam ${builder.problems.length} itens nos três idiomas. Desative a pesquisa para salvar assim mesmo.`
            }
            onClick={builder.save}
          />
        </div>
      </div>

      {/* GLModal keeps its content in the a11y tree when closed, so mount it only when needed. */}
      {isPreviewOpen ? (
        <SurveyPreviewModal
          form={form}
          language={builder.language}
          onClose={() => {
            setIsPreviewOpen(false);
          }}
        />
      ) : null}

      {/*
        R4 said out loud, before the save and not after it: on an approved survey this opens the
        next version, and the one on the air keeps being delivered until the new one is approved.
      */}
      {builder.draftVersionPrompt ? (
        <SurveyDraftVersionDialog
          liveVersion={builder.draftVersionPrompt.liveVersion}
          isWorking={builder.isSaving}
          onConfirm={builder.confirmDraftVersion}
          onClose={builder.dismissDraftVersionPrompt}
        />
      ) : null}

      {/*
        Both options, on every trigger. "Perguntar de novo" used to be refused on an exit
        questionnaire — it was asked at every exit, so there was nothing to put back — and since
        R33 it is asked once per person per version, which makes this button the way to ask
        somebody again. Only the sentence under each option changes, because an exit questionnaire
        has no queue to return to.
      */}
      {builder.versionPrompt ? (
        <SurveyVersionDialog
          responseCount={builder.versionPrompt.responseCount}
          isExitQuestionnaire={form.trigger === 'ON_OPT_OUT'}
          isWorking={builder.isResolvingVersion}
          onChoose={builder.resolveVersion}
          onClose={builder.dismissVersionPrompt}
        />
      ) : null}

      {/*
        The slug is held by a DELETED survey. Offering the restore here is the whole point of the
        conflict carrying an id: the alternative — "escolha outro slug" — leaves the good name
        locked inside a row nobody can see, and the next person hits the same wall.
      */}
      {builder.slugHeldByDeleted ? (
        <RestoreHolderDialog
          identifierNoun='slug'
          identifier={form.slug}
          entityNoun='pesquisa'
          deletedAt={builder.slugHeldByDeleted.deletedAt}
          isRestoring={restoreSurvey.isPending}
          note={
            <>
              A pesquisa restaurada volta <strong>desativada</strong>, com o estado de aprovação
              preservado — restaurar não põe pesquisa no ar.
            </>
          }
          onClose={builder.dismissSlugConflict}
          onRestore={() => {
            const deletedId = builder.slugHeldByDeleted?.deletedSurveyId;
            if (!deletedId) return;

            restoreSurvey.mutate(deletedId, {
              onSuccess: restoredSurvey => {
                builder.dismissSlugConflict();
                /*
                  Straight into the restored survey — it is the one the person was trying to write.
                  `onCreated` is exactly the right callback: it turns this creation route into the
                  edit route of that id, which is what the page needs either way.
                */
                onCreated(restoredSurvey.id);
              },
            });
          }}
        />
      ) : null}

      {/*
        R41 — the way out of an edit that does not go through a reviewer. The `200` carries the
        whole survey, so the form is reseeded from it: the questionnaire on screen belonged to the
        version that just went.
      */}
      {isDiscardingDraft && survey && survey.draftVersion !== null ? (
        <SurveyDiscardDraftDialog
          draftVersion={survey.draftVersion}
          liveVersion={survey.version}
          isPendingApproval={reviewStatus === 'PENDING_APPROVAL'}
          isWorking={discardDraft.isPending}
          onClose={() => {
            setIsDiscardingDraft(false);
          }}
          onDiscard={() => {
            discardDraft.mutate(undefined, {
              onSuccess: discarded => {
                builder.reseedFromServer(discarded);
              },
              onSettled: () => {
                setIsDiscardingDraft(false);
              },
            });
          }}
        />
      ) : null}

      {/*
        R42 — the same locks as the purge, because the damage is a superset of it. Nothing on screen
        moves before the `200`: a `400` deleted nothing, and the numbers have to keep saying so.
      */}
      {isResetting && survey ? (
        <SurveyResetDialog
          slug={survey.slug}
          responseCount={survey.responseCount}
          version={survey.version}
          draftVersion={survey.draftVersion}
          isWorking={resetSurvey.isPending}
          onClose={() => {
            setIsResetting(false);
          }}
          onReset={confirmSlug => {
            resetSurvey.mutate(confirmSlug, {
              onSuccess: () => {
                /*
                  The response is a summary, not a survey, so the form waits for the refetch the
                  hook triggered — the questionnaire it has to show is version 1's, and only the
                  detail carries it.
                */
                builder.reseedFromServer();
              },
              onSettled: () => {
                setIsResetting(false);
              },
            });
          }}
        />
      ) : null}

      {isRejecting ? (
        <SurveyRejectDialog
          isWorking={builder.isModerating}
          onReject={reason => {
            builder.reject(reason);
            setIsRejecting(false);
          }}
          onClose={() => {
            setIsRejecting(false);
          }}
        />
      ) : null}
    </MainContent>
  );
};
